/**
 * Collapses consecutive slashes in a path while optionally preserving trailing slash.
 *
 * @param path - Path string to process
 * @param keepTrailing - Whether to preserve trailing slash
 * @returns Path with collapsed slashes
 *
 * @remarks
 * Does not resolve dot segments ('.' or '..').
 */
const collapseSlashes = (path: string, keepTrailing: boolean): string => {
  if (path.length === 0) return path;

  let out = '';
  let prevSlash = false;

  for (let i = 0; i < path.length; i++) {
    const code = path.charCodeAt(i); // '/'
    if (code === 47) {
      if (!prevSlash) out += '/';
      prevSlash = true;
    } else {
      out += path[i];
      prevSlash = false;
    }
  }

  if (keepTrailing && !out.endsWith('/')) out += '/';
  if (!keepTrailing && out.length > 1 && out.endsWith('/')) out = out.slice(0, -1);

  return out;
};

/**
 * Splits a URL reference into path and suffix (query/fragment).
 *
 * @param input - URL reference to split
 * @returns Object with path and suffix parts
 */
const splitPathAndSuffix = (input: string): { path: string; suffix: string } => {
  const q = input.indexOf('?');
  const h = input.indexOf('#');

  let cut = input.length;
  if (q !== -1 && h !== -1) cut = Math.min(q, h);
  else if (q !== -1) cut = q;
  else if (h !== -1) cut = h;

  return { path: input.slice(0, cut), suffix: input.slice(cut) };
};

/**
 * Normalization options for URL paths.
 */
interface NormalizeUrlPathOptions {
  /**
   * Trailing slash behavior:
   * - `'auto'` (default): Preserve original trailing slash
   * - `true`: Force trailing slash
   * - `false`: Remove trailing slash
   */
  preserveTrailing?: 'auto' | boolean;
}

/**
 * Normalizes a URL or path by collapsing duplicate slashes.
 *
 * @param input - URL or path to normalize
 * @param options - Normalization configuration
 * @returns Normalized URL or path string
 *
 * @throws TypeError if input is a network-path reference (//host/...) without base URL
 *
 * @remarks
 * Handles absolute URLs and standalone paths. Does not resolve dot segments.
 *
 * @example
 * ```
 * // Full URL
 * normalizeUrlPath('https://example.com//path//to///resource/');
 * // Returns: 'https://example.com/path/to/resource/'
 *
 * // Standalone path
 * normalizeUrlPath('/path//to////resource/', { preserveTrailing: true });
 * // Returns: '/path/to/resource/'
 *
 * // Network-path reference (throws)
 * normalizeUrlPath('//example.com//path');
 * // Throws: TypeError
 * ```
 */
export const normalizeUrlPath = (input: string, options: NormalizeUrlPathOptions = {}): string => {
  const preserve = options.preserveTrailing ?? 'auto';

  try {
    const url = new URL(input);
    const hadTrailing = url.pathname.endsWith('/');
    const keep = preserve === 'auto' ? hadTrailing : Boolean(preserve);
    url.pathname = collapseSlashes(url.pathname, keep);
    return url.toString();
  } catch {
    const { path, suffix } = splitPathAndSuffix(input);

    if (path.startsWith('//')) {
      throw new TypeError('Network-path references (//host/...) require a base URL to normalize safely');
    }

    const hadTrailing = path.length > 0 && path.endsWith('/');
    const keep = preserve === 'auto' ? hadTrailing : Boolean(preserve);
    const normalizedPath = collapseSlashes(path, keep);
    return normalizedPath + suffix;
  }
};
